import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * /vault/library — the governed Document Library.
 *
 * Phase 1 of Track A (see /app/memory/PERSON_VESSEL_BRIEF.md).
 * This page catalogs *blank* document definitions. It does not hold
 * submitted employee documents. It does not participate in the
 * Workspace or Vessel interpretation pipeline. It is intentionally
 * spare: no narration, no proposals, no observations. It is a shelf.
 */

const REQUIREMENT_LABEL = {
  required: "required",
  conditional: "conditional",
  optional: "optional",
};
const COLLECTION_LABEL = {
  upload: "upload",
  digital_form: "digital form",
  acknowledgment: "acknowledgment",
  external_signature: "external signature",
};
const SENSITIVITY_COLOR = {
  low: "rgba(180,220,255,0.75)",
  medium: "rgba(255,220,140,0.85)",
  high: "rgba(255,180,120,0.9)",
  restricted: "rgba(255,120,120,0.9)",
};

export default function VaultLibraryPage() {
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState({ canAuthor: false, tested: false });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/vault/library", {
        params: { include_retired: includeRetired },
      });
      setDefs(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [includeRetired]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Probe whether this user may author. We do a harmless dry-run PATCH on
  // a nonexistent id: 403 means "not permitted"; 404 means "permitted but
  // that thing isn't there." Either result answers the permission question
  // without creating any data.
  useEffect(() => {
    (async () => {
      try {
        await api.patch("/vault/library/__probe__nonexistent__", { notes: "probe" });
        setPermission({ canAuthor: true, tested: true });
      } catch (e) {
        const code = e?.response?.status;
        setPermission({ canAuthor: code !== 403, tested: true });
      }
    })();
  }, []);

  return (
    <div
      data-testid="vault-library-page"
      style={{
        minHeight: "100vh",
        background: "#050508",
        color: "rgba(255,255,255,0.88)",
        fontFamily: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
        fontSize: 12,
      }}
    >
      <Header
        canAuthor={permission.canAuthor}
        includeRetired={includeRetired}
        setIncludeRetired={setIncludeRetired}
        onNew={() => setShowCreate(true)}
      />

      <div style={{ padding: "20px 32px 60px" }}>
        <Preamble />
        {error && (
          <div
            data-testid="vault-library-error"
            style={{
              padding: "10px 14px",
              border: "1px solid rgba(255,120,120,0.4)",
              background: "rgba(255,120,120,0.06)",
              color: "rgba(255,200,200,0.9)",
              borderRadius: 4,
              marginBottom: 16,
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        {loading && <div style={emptyStyle}>loading…</div>}

        {!loading && defs.length === 0 && (
          <EmptyState canAuthor={permission.canAuthor} onNew={() => setShowCreate(true)} />
        )}

        {!loading && defs.length > 0 && (
          <div
            data-testid="vault-library-list"
            style={{ display: "flex", flexDirection: "column", gap: 10 }}
          >
            {defs.map((d) => (
              <DefinitionRow
                key={d.id}
                def={d}
                onOpen={() => setSelected(d)}
              />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <DefinitionDetail
          def={selected}
          canAuthor={permission.canAuthor}
          onClose={() => setSelected(null)}
          onChanged={() => {
            refresh();
            setSelected(null);
          }}
        />
      )}
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/* ---------- Header ---------- */
function Header({ canAuthor, includeRetired, setIncludeRetired, onNew }) {
  return (
    <div
      style={{
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        padding: "20px 32px 16px",
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            opacity: 0.55,
            marginBottom: 6,
          }}
        >
          Vault · Document Library
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300 }}>
          Approved blank documents and their requirements.
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <label
          style={{
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            opacity: 0.65,
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
          }}
        >
          <input
            data-testid="vault-library-include-retired"
            type="checkbox"
            checked={includeRetired}
            onChange={(e) => setIncludeRetired(e.target.checked)}
          />
          include retired
        </label>
        {canAuthor && (
          <button
            data-testid="vault-library-new-btn"
            onClick={onNew}
            style={buttonPrimary}
          >
            + Document definition
          </button>
        )}
      </div>
    </div>
  );
}

function Preamble() {
  return (
    <div
      style={{
        marginBottom: 20,
        padding: "12px 14px",
        border: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.015)",
        borderRadius: 4,
        fontFamily: "'Inter', sans-serif",
        fontStyle: "italic",
        fontSize: 12,
        color: "rgba(255,255,255,0.7)",
        lineHeight: 1.6,
      }}
    >
      This library catalogs the <b>blank</b> documents and requirements Rainbow uses to
      establish employment relationships. It does not hold anyone's completed file. That
      is a later phase, in a separate protected path. What you build here answers one
      question: <i>what does a complete employee file look like?</i> Before that is
      answered, no file can honestly be called complete.
    </div>
  );
}

/* ---------- Row ---------- */
function DefinitionRow({ def, onOpen }) {
  return (
    <div
      data-testid={`vault-def-${def.id}`}
      onClick={onOpen}
      style={{
        padding: "12px 14px",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 4,
        background: "rgba(255,255,255,0.02)",
        cursor: "pointer",
        opacity: def.status === "retired" ? 0.5 : 1,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 14, fontFamily: "'Inter', sans-serif", color: "rgba(255,255,255,0.95)" }}>
          {def.name}
          <span style={{ marginLeft: 10, fontSize: 10, letterSpacing: "0.12em", opacity: 0.55 }}>
            v{def.effective_version}
          </span>
          {def.status === "retired" && (
            <span style={{ marginLeft: 8, fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(180,180,200,0.7)" }}>
              retired
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Chip>{REQUIREMENT_LABEL[def.requirement]}</Chip>
          <Chip>{COLLECTION_LABEL[def.collection_method]}</Chip>
          <Chip color={SENSITIVITY_COLOR[def.sensitivity]}>{def.sensitivity}</Chip>
        </div>
      </div>
      <div
        style={{
          marginTop: 6,
          fontFamily: "'Inter', sans-serif",
          fontSize: 12,
          opacity: 0.68,
          lineHeight: 1.5,
        }}
      >
        {def.purpose}
      </div>
      <AppliesLine applies_to={def.applies_to} />
    </div>
  );
}

function AppliesLine({ applies_to }) {
  const parts = [];
  if (applies_to?.all_employees) parts.push("all employees");
  else {
    if (applies_to?.roles?.length) parts.push("roles: " + applies_to.roles.join(", "));
    if (applies_to?.locations?.length) parts.push("locations: " + applies_to.locations.join(", "));
    if (applies_to?.journey_stages?.length) parts.push("stages: " + applies_to.journey_stages.join(", "));
    if (!parts.length) parts.push("scope unspecified");
  }
  return (
    <div style={{ marginTop: 6, fontSize: 10, opacity: 0.5, letterSpacing: "0.05em" }}>
      applies to · {parts.join(" · ")}
    </div>
  );
}

function Chip({ children, color }) {
  return (
    <span
      style={{
        fontSize: 9,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        padding: "3px 8px",
        borderRadius: 10,
        border: `1px solid ${color || "rgba(255,255,255,0.14)"}`,
        color: color || "rgba(255,255,255,0.75)",
      }}
    >
      {children}
    </span>
  );
}

/* ---------- Empty state ---------- */
function EmptyState({ canAuthor, onNew }) {
  return (
    <div
      data-testid="vault-library-empty"
      style={{
        padding: 30,
        border: "1px dashed rgba(255,255,255,0.15)",
        borderRadius: 6,
        textAlign: "left",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div style={{ fontSize: 16, marginBottom: 10, opacity: 0.85 }}>
        The library is empty.
      </div>
      <div style={{ fontSize: 13, opacity: 0.65, marginBottom: 16, lineHeight: 1.6 }}>
        Every document Rainbow currently uses to establish an employment relationship
        should have a definition here — its purpose, who it applies to, whether it is
        required, how it is collected, who stewards it, and its current blank version.
        When the library reflects the actual paperwork, packages can be composed and
        existing files can be reconciled against them.
      </div>
      {canAuthor ? (
        <button data-testid="vault-library-empty-new" onClick={onNew} style={buttonPrimary}>
          + Create the first document definition
        </button>
      ) : (
        <div style={{ fontSize: 12, opacity: 0.55 }}>
          Your role does not permit authoring library definitions. Ask an
          administrator to catalog the documents.
        </div>
      )}
    </div>
  );
}

/* ---------- Create modal ---------- */
function CreateModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [scope, setScope] = useState("all");
  const [rolesText, setRolesText] = useState("");
  const [locationsText, setLocationsText] = useState("");
  const [stagesText, setStagesText] = useState("");
  const [requirement, setRequirement] = useState("required");
  const [collectionMethod, setCollectionMethod] = useState("upload");
  const [sensitivity, setSensitivity] = useState("medium");
  const [steward, setSteward] = useState("");
  const [reviewFreq, setReviewFreq] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setErr(null);
    const applies_to =
      scope === "all"
        ? { all_employees: true }
        : {
            all_employees: false,
            roles: splitCsv(rolesText),
            locations: splitCsv(locationsText),
            journey_stages: splitCsv(stagesText),
          };
    try {
      await api.post("/vault/library", {
        name: name.trim(),
        purpose: purpose.trim(),
        applies_to,
        requirement,
        collection_method: collectionMethod,
        sensitivity,
        steward: steward.trim() || null,
        review_frequency: reviewFreq.trim() || null,
        notes: notes.trim() || null,
      });
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="New document definition" onClose={onClose} testid="vault-library-create-modal">
      <Field label="Name">
        <input data-testid="vault-create-name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Purpose — what it is and why it exists">
        <textarea data-testid="vault-create-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
      </Field>

      <Field label="Applies to">
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <RadioOption label="All employees" checked={scope === "all"} onChange={() => setScope("all")} />
          <RadioOption label="Specific" checked={scope === "specific"} onChange={() => setScope("specific")} />
        </div>
        {scope === "specific" && (
          <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
            <input placeholder="roles (comma-separated, e.g. experience_guide, coo)" value={rolesText} onChange={(e) => setRolesText(e.target.value)} style={inputStyle} />
            <input placeholder="locations (comma-separated)" value={locationsText} onChange={(e) => setLocationsText(e.target.value)} style={inputStyle} />
            <input placeholder="journey stages (comma-separated)" value={stagesText} onChange={(e) => setStagesText(e.target.value)} style={inputStyle} />
          </div>
        )}
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Requirement">
          <select data-testid="vault-create-requirement" value={requirement} onChange={(e) => setRequirement(e.target.value)} style={inputStyle}>
            <option value="required">required</option>
            <option value="conditional">conditional</option>
            <option value="optional">optional</option>
          </select>
        </Field>
        <Field label="Collection method">
          <select data-testid="vault-create-method" value={collectionMethod} onChange={(e) => setCollectionMethod(e.target.value)} style={inputStyle}>
            <option value="upload">upload</option>
            <option value="digital_form">digital form</option>
            <option value="acknowledgment">acknowledgment</option>
            <option value="external_signature">external signature</option>
          </select>
        </Field>
        <Field label="Sensitivity">
          <select data-testid="vault-create-sensitivity" value={sensitivity} onChange={(e) => setSensitivity(e.target.value)} style={inputStyle}>
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
            <option value="restricted">restricted</option>
          </select>
        </Field>
        <Field label="Steward (owning function)">
          <input value={steward} onChange={(e) => setSteward(e.target.value)} placeholder="e.g. Payroll · HR · Manager" style={inputStyle} />
        </Field>
      </div>

      <Field label="Review frequency (optional)">
        <input value={reviewFreq} onChange={(e) => setReviewFreq(e.target.value)} placeholder="e.g. annual · on change · never" style={inputStyle} />
      </Field>
      <Field label="Notes (optional)">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
      </Field>

      {err && <div style={errorStyle}>{err}</div>}

      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onClose} style={buttonGhost}>cancel</button>
        <button
          data-testid="vault-create-submit"
          onClick={submit}
          disabled={submitting || !name.trim() || !purpose.trim()}
          style={{ ...buttonPrimary, opacity: submitting || !name.trim() || !purpose.trim() ? 0.5 : 1 }}
        >
          {submitting ? "creating…" : "Create definition"}
        </button>
      </div>
    </ModalShell>
  );
}

/* ---------- Detail overlay ---------- */
function DefinitionDetail({ def, canAuthor, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    api.get(`/vault/library/${def.id}/versions`).then((r) => setVersions(r.data)).catch(() => {});
  }, [def.id]);

  const retire = async () => {
    if (!window.confirm("Retire this document definition? It will no longer appear in the active library.")) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/vault/library/${def.id}/retire`);
      onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadTemplate = async (file) => {
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      await api.post(`/vault/library/${def.id}/template`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeTemplate = async () => {
    if (!window.confirm("Remove the attached blank template?")) return;
    setBusy(true);
    setErr(null);
    try {
      await api.delete(`/vault/library/${def.id}/template`);
      onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const templateUrl = def.template_file_id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/vault/library/${def.id}/template`
    : null;

  return (
    <ModalShell title={def.name} onClose={onClose} testid="vault-library-detail" wide>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <Chip>v{def.effective_version}</Chip>
        <Chip>{REQUIREMENT_LABEL[def.requirement]}</Chip>
        <Chip>{COLLECTION_LABEL[def.collection_method]}</Chip>
        <Chip color={SENSITIVITY_COLOR[def.sensitivity]}>{def.sensitivity}</Chip>
        <Chip>{def.status}</Chip>
      </div>

      <Section label="Purpose">{def.purpose}</Section>
      <Section label="Applies to">
        <AppliesLine applies_to={def.applies_to} />
      </Section>
      {def.steward && <Section label="Steward">{def.steward}</Section>}
      {def.review_frequency && <Section label="Review frequency">{def.review_frequency}</Section>}
      {def.conditional_note && <Section label="Conditional note">{def.conditional_note}</Section>}
      {def.notes && <Section label="Notes">{def.notes}</Section>}

      <Section label="Blank template">
        {def.template_file_id ? (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <a
              href={templateUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: "rgba(180,220,255,0.9)", textDecoration: "underline", fontSize: 12 }}
              data-testid="vault-detail-template-download"
            >
              {def.template_filename || "template"}
            </a>
            <span style={{ fontSize: 10, opacity: 0.55 }}>{Math.round((def.template_size || 0) / 1024)} kB · {def.template_mime}</span>
            {canAuthor && def.status === "active" && (
              <button onClick={removeTemplate} disabled={busy} style={buttonGhost}>remove template</button>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ opacity: 0.5, fontSize: 12, fontStyle: "italic" }}>no blank template attached</span>
            {canAuthor && def.status === "active" && (
              <label
                data-testid="vault-detail-template-upload-label"
                style={{ ...buttonGhost, cursor: "pointer" }}
              >
                attach blank template
                <input
                  data-testid="vault-detail-template-upload-input"
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    uploadTemplate(f);
                  }}
                />
              </label>
            )}
          </div>
        )}
      </Section>

      {versions.length > 1 && (
        <Section label="Version history">
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {versions.map((v) => (
              <div key={v.id} style={{ fontSize: 11, opacity: v.status === "retired" ? 0.5 : 1 }}>
                <b>v{v.effective_version}</b>{"  ·  "}{v.status}{"  ·  "}{new Date(v.created_at).toLocaleString()}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section label="Provenance">
        <div style={{ fontSize: 10, opacity: 0.55, lineHeight: 1.5 }}>
          created {new Date(def.created_at).toLocaleString()} by {def.created_by}
          <br />
          updated {new Date(def.updated_at).toLocaleString()} by {def.updated_by}
        </div>
      </Section>

      {err && <div style={errorStyle}>{err}</div>}

      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div>
          {canAuthor && def.status === "active" && (
            <button
              data-testid="vault-detail-retire"
              onClick={retire}
              disabled={busy}
              style={{ ...buttonGhost, borderColor: "rgba(255,160,160,0.4)", color: "rgba(255,180,180,0.9)" }}
            >
              retire this version
            </button>
          )}
        </div>
        <button onClick={onClose} style={buttonPrimary}>close</button>
      </div>
    </ModalShell>
  );
}

/* ---------- shared bits ---------- */
function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.55, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, lineHeight: 1.55, color: "rgba(255,255,255,0.85)" }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.55, marginBottom: 6 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function RadioOption({ label, checked, onChange }) {
  return (
    <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, cursor: "pointer" }}>
      <input type="radio" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}

function ModalShell({ title, onClose, testid, children, wide }) {
  return (
    <div
      onClick={onClose}
      data-testid={testid}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: 20,
        overflow: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0c0c14",
          border: "1px solid rgba(255,255,255,0.14)",
          borderRadius: 6,
          padding: 24,
          width: `min(${wide ? 640 : 520}px, 96vw)`,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 300, marginBottom: 12 }}>
          {title}
        </div>
        {children}
      </div>
    </div>
  );
}

function splitCsv(s) {
  return (s || "").split(",").map((x) => x.trim()).filter(Boolean);
}

/* ---------- styles ---------- */
const inputStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.14)",
  color: "inherit",
  padding: "8px 10px",
  fontSize: 12,
  fontFamily: "inherit",
  borderRadius: 3,
  outline: "none",
  boxSizing: "border-box",
};
const buttonPrimary = {
  background: "rgba(180,240,200,0.12)",
  color: "rgba(200,240,215,0.95)",
  border: "1px solid rgba(180,240,200,0.4)",
  padding: "8px 14px",
  borderRadius: 3,
  fontSize: 12,
  cursor: "pointer",
  fontFamily: "inherit",
};
const buttonGhost = {
  background: "rgba(255,255,255,0.06)",
  color: "rgba(255,255,255,0.75)",
  border: "1px solid rgba(255,255,255,0.14)",
  padding: "6px 12px",
  borderRadius: 3,
  fontSize: 11,
  cursor: "pointer",
  fontFamily: "inherit",
};
const errorStyle = {
  marginTop: 12,
  padding: "8px 10px",
  border: "1px solid rgba(255,120,120,0.4)",
  background: "rgba(255,120,120,0.06)",
  color: "rgba(255,200,200,0.9)",
  fontSize: 11,
  borderRadius: 4,
};
const emptyStyle = { fontSize: 12, opacity: 0.5, fontStyle: "italic" };
